import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Button, Icon } from "antd";
import ZoomableImage from "components/ZoomableImage";
import _ from "lodash";
import { absoluteTimeLookup, asyncLoadImage, getUrlDataForFrame, videoFrameExtractor, videoTimestampExtractor } from "components/util/PlaylistUtils";
import { useSelector } from "react-redux";
import moment from "moment";
import OBCSpinner from "components/util/OBC";
import { coordinateToTimestampV2 } from "components/util/GeometryUtilsV2";

const csrfTokenSelector = (state) => state.csrfToken;

const BestMatchOlePatrolComponent = ({ playlist, playlistUrls, sessionData, bestMatchChecked, currentPositionCoords }) => {
    const imageUrlToDisplay = useRef(null);
    const csrfToken = useSelector(csrfTokenSelector);

    const [zoom, setZoom] = useState(1);
    const [imageData, setImageData] = useState(null);
    const [sourceList, setSourceList] = useState([]);
    const [sourceIndex, setSourceIndex] = useState(0);
    const [manualTimestampOffset, setManualTimestampOffset] = useState(0);
    const [currentTimestampToUse, setCurrentTimestampToUse] = useState(null);
    const [bestMatchInitialized, setBestMatchInitialized] = useState(false);

    const [canGoBackward, setCanGoBackward] = useState(false);
    const [canGoForward, setCanGoForward] = useState(false);

    const streamInfo = useMemo(() => {
        return _.get(sessionData, ["stream_info"], []);
    }, [sessionData]);

    const deviceName = useMemo(() => {
        return _.get(sessionData, "device_name", null);
    }, [sessionData]);

    const recordingDate = useMemo(() => {
        const recordingDateRaw = _.get(sessionData, "first_seen");
        if (recordingDateRaw) {
            return moment.unix(recordingDateRaw).format("DD MMM YYYY");
        }
        return "Unknown Date";
    }, [sessionData]);

    const imageKeys = useMemo(() => {
        return _.get(playlist, ["video_playlists", sourceIndex], []);
    }, [playlist, sourceIndex]);

    const routeGeomData = useMemo(() => {
        return _.get(playlist, ["route_locations_v2"], []);
    }, [playlist]);

    const baseURL = useMemo(() => {
        return playlistUrls?.base;
    }, [playlistUrls]);

    useEffect(() => {
        if (imageKeys.length === 0) {
            setBestMatchInitialized(true);
        }
    }, [imageKeys]);

    useEffect(() => {
        if (!routeGeomData || !currentPositionCoords) return;

        // given patrol position location lookup
        const timestamp = coordinateToTimestampV2(currentPositionCoords, routeGeomData);
        if (timestamp) {
            setCurrentTimestampToUse(timestamp * 1000);
            if (!bestMatchInitialized) {
                setBestMatchInitialized(true);
            }
        } else {
            setCurrentTimestampToUse(null);
        }
    }, [bestMatchInitialized, currentPositionCoords, routeGeomData]);

    useEffect(() => {
        const sources = [];

        if (!playlist || !playlist.video) {
            setSourceList([]);
            return;
        }

        for (let i = 0; i < playlist.video.length; i++) {
            if (!playlist.video[i].length) {
                continue;
            }

            const sourceStreamData = _.get(streamInfo, [i], {});
            const sourceStreamInfo = _.get(sourceStreamData, ["info"], {});
            const archived = _.get(sourceStreamData, "archived", 0);
            if (archived === 1) {
                continue;
            }

            const sourceName = _.get(sourceStreamInfo, ["type"], "Camera " + (i + 1));
            const sourceLabel = _.get(sourceStreamInfo, ["label"], "");

            let name = sourceName;
            if (sourceLabel) {
                name += " (" + sourceLabel + ")";
            }

            const source = {
                name: name,
                streams: [
                    {
                        name: "Still Images",
                        videoIndex: i,
                        isVideo: true,
                        isEnhanced: false,
                        isStills: true,
                    },
                ],
            };

            sources.push(source);
        }

        setSourceIndex(_.get(sources, "[0].streams[0].videoIndex"));
        setSourceList(sources);
    }, [playlist, setSourceIndex, streamInfo]);

    useEffect(() => {
        if (!sourceList || !sourceList.length) {
            setImageData(null);
        }
    }, [sourceList]);

    const getUrlDataForImage = useCallback(
        (index, subIndex) => {
            if (index !== -1) {
                let selectedKey = imageKeys[index];

                if (selectedKey) {
                    let imageKey = selectedKey[0];
                    let imageFile = imageKey;
                    let range = null;

                    const urlFrameData = getUrlDataForFrame(imageKeys, index, subIndex);
                    imageFile = urlFrameData.imageFile;
                    range = urlFrameData.range;

                    let url = baseURL + imageFile;

                    if (csrfToken) {
                        url += `?csrf=${csrfToken}`;
                    }

                    return {
                        url,
                        range,
                        cacheKey: `${url}.${subIndex}`,
                    };
                }
            }
            return null;
        },
        [baseURL, csrfToken, imageKeys],
    );

    useEffect(() => {
        if (!currentTimestampToUse) return;

        let timestampToUse = parseInt(currentTimestampToUse + manualTimestampOffset) / 1000;
        let indexToUse = absoluteTimeLookup(timestampToUse, imageKeys);
        const playlistItem = imageKeys[indexToUse];

        if (!playlistItem) {
            return;
        }

        let frameCount = videoFrameExtractor(playlistItem);
        const videoStartTimestamp = videoTimestampExtractor(playlistItem);
        const videoEndTimestamp = _.get(playlistItem, [3, 1], null);

        const keyDuration = videoEndTimestamp - videoStartTimestamp;
        const keyProgress = (timestampToUse - videoStartTimestamp) / keyDuration;

        let subIndex = Math.floor(keyProgress * frameCount);
        subIndex = Math.min(Math.max(1, subIndex), frameCount - 1);

        if (subIndex > frameCount) {
            subIndex = subIndex % frameCount;
            indexToUse += 1;
        } else if (subIndex < 0) {
            subIndex = frameCount + subIndex;
            indexToUse -= 1;
        }

        // enable/disable backward buttons
        if (indexToUse === 0 && subIndex === 1) {
            setCanGoBackward(false);
        } else {
            setCanGoBackward(true);
        }

        // enable/disable forward buttons
        if (indexToUse === imageKeys.length - 1 && subIndex === frameCount - 1) {
            setCanGoForward(false);
        } else {
            setCanGoForward(true);
        }

        const urlData = getUrlDataForImage(indexToUse, subIndex);

        if (!urlData) {
            return;
        }

        imageUrlToDisplay.current = urlData.cacheKey;
        asyncLoadImage(urlData.url, urlData.range, (imageData) => {
            if (urlData.cacheKey === imageUrlToDisplay.current) {
                setImageData(imageData);
            }
        });
    }, [getUrlDataForImage, imageKeys, manualTimestampOffset, bestMatchInitialized, currentTimestampToUse]);

    const adjustmentComponent = useMemo(() => {
        return (
            <div className="BestMatchAdjustmentControls">
                <Button
                    disabled={!canGoBackward}
                    size="small"
                    onClick={() => {
                        setManualTimestampOffset((prevState) => prevState - 100);
                    }}>
                    <div className="BestMatchButtonContent">
                        <Icon type="fast-backward" />
                        <div>Backward</div>
                    </div>
                </Button>

                <Button
                    size="small"
                    disabled={!canGoForward}
                    onClick={() => {
                        setManualTimestampOffset((prevState) => prevState + 100);
                    }}>
                    <div className="BestMatchButtonContent">
                        <div>Forward</div>
                        <Icon type="fast-forward" />
                    </div>
                </Button>
            </div>
        );
    }, [canGoBackward, canGoForward]);

    if (!bestMatchChecked) {
        return (
            <div className="OlePatrolsPageSidePanelSourceItem BestMatchSession">
                <div className="BestMatchStatusMessage">Looking for supporting session...</div>
            </div>
        );
    }

    if (!deviceName) {
        return (
            <div className="OlePatrolsPageSidePanelSourceItem BestMatchSession">
                <div className="BestMatchStatusMessage">No supporting session found.</div>
            </div>
        );
    }

    if (bestMatchChecked && !bestMatchInitialized) {
        return (
            <div className="OlePatrolsPageSidePanelSourceItem BestMatchSession">
                <div className="BestMatchStatusMessage BestMatchStatusMessageLoading">
                    <OBCSpinner
                        color={"#e8dfff"}
                        size={13}
                    />
                    <div>Loading supporting session...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="OlePatrolsPageSidePanelSourceItem BestMatchSession">
            <div className="BestMatchPlayerContainer">
                <div className="BestMatchPlayerControls">
                    <div className="BestMatchDeviceInfo">
                        <span className="BestMatchDeviceName">{deviceName}</span>
                        <div>{recordingDate}</div>
                    </div>
                    <div>{adjustmentComponent}</div>
                </div>

                <ZoomableImage
                    imgSrc={imageData}
                    zoom={zoom}
                    updateZoom={(zoom) => setZoom(zoom)}
                    withSpinner={true}
                />
            </div>
        </div>
    );
};

export default BestMatchOlePatrolComponent;
